<template>
  <Container>
    <PageHeader :items="items" />

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="d-flex flex-wrap align-items-center justify-content-between">
              <h4 class="card-title">Lodgings</h4>
              <b-button v-b-modal.lodging-modal @click="clearLodging" variant="outline-primary">Add Lodging</b-button>
            </div>

            <b-modal size="lg" id="lodging-modal" title="Enter Lodging Details" title-class="font-18">
              <ValidationObserver ref="form" v-slot="{ handleSubmit }">
                <b-form id="lodging-form" @submit.prevent="handleSubmit(onSubmit)">
                  <b-form-group label="Name" label-for="name">
                    <ValidationProvider rules="required" v-slot="{ errors }">
                      <b-form-input v-model="lodging.name" id="name" placeholder="Enter name"></b-form-input>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </ValidationProvider>
                  </b-form-group>

                  <b-row>
                    <b-col sm="6">
                      <b-form-group label="Country" label-for="country">
                        <ValidationProvider rules="required" v-slot="{ errors }">
                          <Multiselect
                            id="country"
                            v-model="lodging.country"
                            :options="countriesName"
                            track-by="value"
                            label="text"
                            :allow-empty="false"
                            @input="populateRegions"
                            deselect-label="Selected"
                          ></Multiselect>

                          <small class="text-danger">{{ errors[0] }}</small>
                        </ValidationProvider>
                      </b-form-group>
                    </b-col>

                    <b-col sm="6">
                      <b-form-group label="Region" label-for="region">
                        <ValidationProvider rules="required" v-slot="{ errors }">
                          <Multiselect
                            id="region"
                            v-model="lodging.region_id"
                            :options="regionsName"
                            track-by="value"
                            label="text"
                            :allow-empty="false"
                            :show-labels="false"
                          ></Multiselect>

                          <small class="text-danger">{{ errors[0] }}</small>
                        </ValidationProvider>
                      </b-form-group>
                    </b-col>
                  </b-row>

                  <b-row>
                    <b-col sm="6">
                      <b-form-group label="Maximum guests" label-for="max_guests">
                        <ValidationProvider rules="required|minimum:2" v-slot="{ errors }">
                          <b-form-input
                            v-model="lodging.max_guests"
                            type="number"
                            id="max_guests"
                            placeholder="Enter maximum guests"
                          ></b-form-input>

                          <small class="text-danger">{{ errors[0] }}</small>
                        </ValidationProvider>
                      </b-form-group>
                    </b-col>

                    <b-col sm="6">
                      <b-form-group label="Base price" label-for="base_price">
                        <ValidationProvider rules="required|positive" v-slot="{ errors }">
                          <b-form-input
                            type="number"
                            step=".01"
                            min="0"
                            v-model="lodging.base_price"
                            id="base_price"
                            placeholder="Enter base price"
                          ></b-form-input>

                          <small class="text-danger">{{ errors[0] }}</small>
                        </ValidationProvider>
                      </b-form-group>
                    </b-col>
                  </b-row>

                  <b-row>
                    <b-col sm="6">
                      <b-form-group label="Checkin Time" label-for="checkin">
                        <ValidationProvider rules="required" v-slot="{ errors }">
                          <DatePicker
                            v-model="lodging.checkin_time"
                            class="w-100"
                            id="checkin"
                            format="hh:mm a"
                            value-type="format"
                            type="time"
                            placeholder="hh:mm a"
                          ></DatePicker>

                          <small class="text-danger">{{ errors[0] }}</small>
                        </ValidationProvider>
                      </b-form-group>
                    </b-col>

                    <b-col sm="6">
                      <b-form-group label="Checkout Time" label-for="checkout">
                        <ValidationProvider rules="required" v-slot="{ errors }">
                          <DatePicker
                            v-model="lodging.checkout_time"
                            class="w-100"
                            id="checkout"
                            format="hh:mm a"
                            value-type="format"
                            type="time"
                            placeholder="hh:mm a"
                          ></DatePicker>

                          <small class="text-danger">{{ errors[0] }}</small>
                        </ValidationProvider>
                      </b-form-group>
                    </b-col>
                  </b-row>

                  <b-row>
                    <b-col sm="6">
                      <b-form-group label="Block days before booking" label-for="block_before">
                        <ValidationProvider rules="minmax:0,10" v-slot="{ errors }">
                          <b-form-input
                            type="number"
                            min="0"
                            max="10"
                            v-model="lodging.block_before"
                            id="block_before"
                            placeholder="Default: 0"
                          ></b-form-input>

                          <small class="text-danger">{{ errors[0] }}</small>
                        </ValidationProvider>
                      </b-form-group>
                    </b-col>

                    <b-col sm="6">
                      <b-form-group label="Block days after booking" label-for="block_after">
                        <ValidationProvider rules="minmax:0,10" v-slot="{ errors }">
                          <b-form-input
                            type="number"
                            min="0"
                            max="10"
                            v-model="lodging.block_after"
                            id="block_after"
                            placeholder="Default: 0"
                          ></b-form-input>

                          <small class="text-danger">{{ errors[0] }}</small>
                        </ValidationProvider>
                      </b-form-group>
                    </b-col>
                  </b-row>

                  <b-form-group label="Color" label-for="color">
                    <ValidationProvider rules="required" v-slot="{ errors }">
                      <b-form-input id="color" type="color" v-model="lodging.color"></b-form-input>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </ValidationProvider>
                  </b-form-group>

                  <b-form-group>
                    <div class="inner-repeater mb-4">
                      <div class="inner form-group">
                        <label>Highlights</label>
                        <div v-for="(data, index) in highlights" :key="data.id" class="inner mb-3 row">
                          <div class="col-md-10 col-8">
                            <input
                              v-model="data.highlight"
                              type="text"
                              class="inner form-control"
                              placeholder="Enter highlight"
                            />
                          </div>
                          <div class="col-md-2 col-4">
                            <input
                              type="button"
                              class="btn btn-danger btn-block inner"
                              value="Delete"
                              @click="deleteHighlight(index)"
                            />
                          </div>
                        </div>
                      </div>
                      <input type="button" class="btn btn-success inner" value="Add Highlight" @click="addHighlight" />
                    </div>
                  </b-form-group>

                  <b-form-group class="ml-0" label="Short Description" label-for="description">
                    <ValidationProvider rules="required" v-slot="{ errors }">
                      <textarea
                        id="description"
                        v-model="lodging.description"
                        class="form-control"
                        rows="5"
                        placeholder="Enter short description"
                      ></textarea>

                      <small class="text-danger">{{ errors[0] }}</small>
                    </ValidationProvider>
                  </b-form-group>
                </b-form>
              </ValidationObserver>

              <template v-slot:modal-footer>
                <b-button variant="secondary" @click="$bvModal.hide('lodging-modal')">Close</b-button>
                <b-button form="lodging-form" type="submit" variant="primary">Save</b-button>
              </template>
            </b-modal>

            <div class="row mt-4">
              <div class="col-sm-12 col-md-6">
                <div id="tickets-table_length" class="dataTables_length mb-3">
                  <label class="d-inline-flex align-items-center">
                    Show&nbsp;
                    <b-form-select class="mx-1" v-model="perPage" size="sm" :options="pageOptions"></b-form-select
                    >&nbsp;entries
                  </label>
                </div>
              </div>

              <div class="col-sm-12 col-md-6">
                <div id="tickets-table_filter" class="dataTables_filter text-md-right">
                  <label class="d-inline-flex align-items-center">
                    Search:
                    <b-form-input
                      v-model="filter"
                      type="search"
                      placeholder="Search..."
                      class="form-control form-control-sm ml-2"
                    ></b-form-input>
                  </label>
                </div>
              </div>
            </div>

            <div class="table-responsive mb-0">
              <b-table
                :items="lodgingItems"
                :fields="fields"
                responsive="sm"
                :per-page="perPage"
                :current-page="currentPage"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                :filter="filter"
                :filter-included-fields="filterOn"
                @filtered="onFiltered"
              >
                <template v-slot:cell(status)="row">
                  <b-badge pill :variant="getBadge(row.item.status)">{{ row.item.status }}</b-badge>
                </template>

                <template v-slot:cell(actions)="row">
                  <b-dropdown variant="default" dropleft>
                    <template v-slot:button-content>
                      <i class="fas fa-bars"></i>
                      <i class="bx bx-caret-down"></i>
                    </template>

                    <b-dropdown-item v-b-modal.lodging-modal @click="toggleEdit(row.item.id)">
                      <i class="fa fa-pencil-alt"></i>
                      Edit
                    </b-dropdown-item>

                    <b-dropdown-item :to="{ path: 'lodgings/' + row.item.id }">
                      <i class="fa fa-info-circle"></i>
                      Details
                    </b-dropdown-item>

                    <b-dropdown-item
                      v-b-modal.status-modal
                      v-show="row.item.status != 'active'"
                      @click="toggleEdit(row.item.id)"
                    >
                      <i class="fas fa-check-circle"></i>
                      Activate
                    </b-dropdown-item>

                    <b-dropdown-item
                      v-show="row.item.status == 'active'"
                      v-b-modal.status-modal
                      @click="toggleEdit(row.item.id)"
                      link-class="text-danger"
                    >
                      <i class="fa fa-power-off"></i>
                      Deactivate
                    </b-dropdown-item>
                  </b-dropdown>
                </template>
              </b-table>
            </div>

            <b-modal id="status-modal" title-class="font-18">
              <template v-if="lodging.status == 'active'" v-slot:modal-title>Deactivate Lodging</template>
              <template v-else v-slot:modal-title>Activate Lodging</template>
              <template v-if="lodging.status == 'active'" v-slot:default>
                Are you sure you want to deactivate booking at this lodging?
              </template>
              <template v-else v-slot:default>Are you sure you want to activate booking at this lodging?</template>
              <template v-slot:modal-footer>
                <b-button variant="secondary" @click="$bvModal.hide('status-modal')">No</b-button>
                <b-button variant="primary" @click="changeStatus">Yes</b-button>
              </template>
            </b-modal>

            <div class="row">
              <div class="col">
                <div class="dataTables_paginate paging_simple_numbers float-right">
                  <ul class="pagination pagination-rounded mb-0">
                    <b-pagination v-model="currentPage" :total-rows="rows" :per-page="perPage"></b-pagination>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Container>
</template>

<script>
import Container from "../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import { getLodgings, getCountries, getRegions, createLodging, updateLodging } from "@/store/api";
import moment from "moment";

export default {
  page: {
    title: "Lodgings",
    meta: [{ name: "description", content: appConfig.description }],
  },

  components: {
    Container,
    PageHeader,
  },

  data() {
    return {
      lodgings: [],
      countries: [],
      regions: [],
      lodging: {
        name: "",
        base_price: "",
        max_guests: "",
        status: 1,
        checkin_time: "",
        checkout_time: "",
        country: "",
        region_id: "",
        block_before: "",
        block_after: "",
        color: "#556ee6",
        highlights: [],
        description: "",
      },
      highlights: [{ id: 1 }],
      update: false,
      lodging_id: "",
      items: [
        {
          text: "Home",
          to: "/",
        },
        {
          text: "Lodgings",
          active: true,
        },
      ],
      totalRows: 1,
      currentPage: 1,
      perPage: 10,
      pageOptions: [10, 25, 50, 100],
      filter: null,
      filterOn: [],
      sortBy: "",
      sortDesc: false,
      fields: [
        { key: "name" },
        { key: "base_price" },
        { key: "status" },
        { key: "checkin_time" },
        { key: "checkout_time" },
        { key: "country" },
        { key: "region" },
        { key: "actions", tdClass: "text-right" },
      ],
    };
  },

  created() {
    getLodgings().then((response) => (this.lodgings = response));
    getCountries().then((response) => (this.countries = response));
  },

  mounted() {
    this.totalRows = this.items.length;
  },

  computed: {
    rows() {
      return this.lodgingItems.length;
    },

    countriesName() {
      return this.countries.map((country) => {
        return { value: country.id, text: country.attributes.name };
      });
    },

    regionsName() {
      return this.regions.map((region) => {
        return { value: region.id, text: region.attributes.name };
      });
    },

    lodgingItems() {
      return this.lodgings.map((lodging) => {
        return {
          id: lodging.id,
          name: lodging.attributes.name,
          base_price: lodging.attributes.base_price,
          status: lodging.attributes.status,
          checkin_time: this.convertToTime(lodging.attributes.checkin_time),
          checkout_time: this.convertToTime(lodging.attributes.checkout_time),
          country: lodging.attributes.country.name,
          region: lodging.attributes.region.name,
        };
      });
    },
  },

  methods: {
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },

    convertToTime(date) {
      return moment(date).format("hh:mm a");
    },

    addHighlight() {
      this.highlights.push({ highlight: "" });
    },

    deleteHighlight(index) {
      this.highlights.splice(index, 1);
    },

    populateRegions() {
      this.lodging.region_id = "";
      getRegions(this.lodging.country.value).then((response) => (this.regions = response));
    },

    clearLodging() {
      this.update = false;
      this.lodging_id = "";
      this.action = true;
      this.regions = [];
      this.highlights = [{ id: 1 }];
      this.lodging.name = "";
      this.lodging.base_price = "";
      this.lodging.max_guests = "";
      this.lodging.status = 1;
      this.lodging.checkin_time = "";
      this.lodging.checkout_time = "";
      this.lodging.country = "";
      this.lodging.region_id = "";
      this.lodging.block_before = "";
      this.lodging.block_after = "";
      this.lodging.color = "#556ee6";
    },

    toggleEdit(id) {
      this.clearLodging();
      let index = 0;
      this.update = true;
      this.lodging_id = id;
      let selectedLodging = this.lodgings.find((lodging) => lodging.id == id);
      this.lodging.name = selectedLodging.attributes.name;
      this.lodging.country = {
        value: selectedLodging.attributes.country.id,
        text: selectedLodging.attributes.country.name,
      };
      this.populateRegions();
      this.lodging.region_id = {
        value: selectedLodging.attributes.region.id,
        text: selectedLodging.attributes.region.name,
      };
      if (selectedLodging.attributes.highlights != null && selectedLodging.attributes.highlights.length) {
        this.highlights = selectedLodging.attributes.highlights.map((highlight) => {
          index++;
          return { id: index, highlight: highlight };
        });
      }
      this.lodging.base_price = selectedLodging.attributes.base_price;
      this.lodging.status = selectedLodging.attributes.status;
      this.lodging.checkin_time = this.convertToTime(selectedLodging.attributes.checkin_time);
      this.lodging.checkout_time = this.convertToTime(selectedLodging.attributes.checkout_time);
      this.lodging.max_guests = selectedLodging.attributes.max_guests;
      this.lodging.block_before = selectedLodging.attributes.block_before;
      this.lodging.block_after = selectedLodging.attributes.block_after;
      this.lodging.color = selectedLodging.attributes.color;
      this.lodging.description = selectedLodging.attributes.description;
    },

    getBadge(status) {
      return status == "active" ? "primary" : "danger";
    },

    changeStatus() {
      this.$bvModal.hide("status-modal");
      let lodging = { status: 1 };
      if (this.lodging.status == "active") lodging = { status: 0 };
      this.onUpdateLodging(lodging);
    },

    onUpdateLodging(lodging) {
      updateLodging(lodging, this.lodging_id).then((response) => {
        let index = this.lodgings.findIndex((lodging) => lodging.id === this.lodging_id);
        this.lodgings.splice(index, 1);
        this.lodgings.splice(index, 0, response);
      });
    },

    onSubmit() {
      this.$bvModal.hide("lodging-modal");
      this.lodging.country = this.lodging.country.value;
      this.lodging.region_id = this.lodging.region_id.value;
      this.lodging.highlights = this.highlights.map((highlight) => {
        return highlight.highlight;
      });
      if (this.update) {
        this.onUpdateLodging(this.lodging);
      } else {
        createLodging(this.lodging).then((response) => this.lodgings.push(response));
      }
    },
  },
};
</script>
